/**
 * Syncs auth state to observability context for business-web: setContext once
 * the identity is known, updateContext when the tenant settles, resetContext on
 * logout. Must be mounted inside ObservabilityProvider, the SDK providers and
 * AuthProvider.
 *
 * W0.3 · F14 / G14 — this portal had NO sync at all: every Sentry and PostHog
 * event from the company portal arrived without a user, a tenant or a group,
 * so nothing from business-web could be attributed to the company that hit it.
 *
 * Identity contract (the same one journey-web and citizen-mobile use):
 *  • `userId` is the INTERNAL `users.id` UUID from `/users/me` — not Clerk's
 *    opaque `user_…` id.
 *  • `tenantId` is the user's `user_tenants` row with `isDefault = true`, else
 *    any membership, read from `/auth/me/portals` (which INNER JOINs `tenants`,
 *    so the id provably exists there). It is the same row `AuthGuard` resolves
 *    for this user's requests, so the tag matches the tenant the API filtered on.
 *  • Until the tenant resolves the tag is omitted — a missing tag is honest, a
 *    wrong tag is not.
 */

import { resolveTenantId, useObservabilityContext } from '@tonnie/observability';
import { useMyPortals, useUserProfile } from '@tonnie/sdk';
import { useEffect, useRef } from 'react';
import { useAuth } from '@/core/contexts/AuthContext';
import { OBSERVABILITY_ENV, OBSERVABILITY_RELEASE } from '@/core/lib/observability-env';

export function ObservabilityAuthSync(): null {
  const { user } = useAuth();
  const { data: profile } = useUserProfile();
  const { data: portalsData } = useMyPortals();
  const { setContext, updateContext, resetContext } = useObservabilityContext();

  const internalUserId = profile?.id;
  const tenantId = resolveTenantId(portalsData?.portals);

  const identifiedUserIdRef = useRef<string | null>(null);
  const taggedTenantIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user || !internalUserId) {
      if (identifiedUserIdRef.current !== null) {
        resetContext();
        identifiedUserIdRef.current = null;
        taggedTenantIdRef.current = null;
      }
      return;
    }

    if (identifiedUserIdRef.current !== internalUserId) {
      identifiedUserIdRef.current = internalUserId;
      taggedTenantIdRef.current = tenantId ?? null;
      setContext({
        userId: internalUserId,
        ...(tenantId ? { tenantId } : {}),
        // Everyone on this portal acts on behalf of a company.
        persona: 'company',
        portal: 'business',
        env: OBSERVABILITY_ENV,
        release: OBSERVABILITY_RELEASE,
      });
      return;
    }

    if (tenantId && tenantId !== taggedTenantIdRef.current) {
      taggedTenantIdRef.current = tenantId;
      updateContext({ tenantId });
    }
  }, [user, internalUserId, tenantId, setContext, updateContext, resetContext]);

  return null;
}
